/**
 * TR-808 Rhythm Composer
 * Émulation de la boîte à rythmes Roland TR-808
 */

import * as Tone from 'tone';

export class TR808 {
    constructor(audioEngine, options = {}) {
        this.audioEngine = audioEngine;
        this.performance = options.performance || { lowPower: false };

        // État power (pour économiser les ressources)
        this.isPowered = true;
        this.mixerChannel = null;

        // Paramètres par défaut - organisés par instrument
        this.params = {
            // KICK
            kickLevel: 0.8,
            kickTone: 50,
            kickDecay: 0.5,
            // SNARE
            snareLevel: 0.8,
            snareTone: 0.5,
            snareSnappy: 0.5,
            // CLAP
            clapLevel: 0.7,
            clapDecay: 0.15,
            // RIMSHOT
            rimshotLevel: 0.7,
            rimshotTune: 0.5,
            // HI-HATS
            hhLevel: 0.7,
            hhDecay: 0.08,
            openHHLevel: 0.7,
            openHHDecay: 0.3,
            // TOMS
            lowTomLevel: 0.7,
            lowTomTune: 0.5,
            midTomLevel: 0.7,
            midTomTune: 0.5,
            hiTomLevel: 0.7,
            hiTomTune: 0.5,
            // COWBELL
            cowbellLevel: 0.6
        };

        this.setupAudio();
    }

    setupAudio() {
        // Connecter au canal du mixer (le mixer gère les sends)
        this.mixerChannel = this.audioEngine.getMixerChannelInput('tr808');

        // Channel master pour tous les drums → mixer
        this.masterChannel = new Tone.Gain(1);
        this.masterChannel.connect(this.mixerChannel);

        // === KICK 808 ===
        this.kickGain = new Tone.Gain(this.params.kickLevel).connect(this.masterChannel);
        this.kick = new Tone.MembraneSynth({
            pitchDecay: 0.05,
            octaves: 6,
            oscillator: { type: 'sine' },
            envelope: {
                attack: 0.001,
                decay: this.params.kickDecay,
                sustain: 0,
                release: 0.1
            }
        }).connect(this.kickGain);

        // === SNARE ===
        this.snareGain = new Tone.Gain(this.params.snareLevel).connect(this.masterChannel);

        this.snareNoise = new Tone.NoiseSynth({
            noise: { type: 'white' },
            envelope: {
                attack: 0.001,
                decay: 0.2,
                sustain: 0,
                release: 0.1
            }
        }).connect(this.snareGain);

        this.snareTone = new Tone.MembraneSynth({
            pitchDecay: 0.01,
            octaves: 4,
            envelope: {
                attack: 0.001,
                decay: 0.1,
                sustain: 0,
                release: 0.1
            }
        }).connect(this.snareGain);
        this.snareTone.volume.value = -10;

        // === CLAP ===
        this.clapGain = new Tone.Gain(this.params.clapLevel).connect(this.masterChannel);

        this.clapFilter = new Tone.Filter({
            frequency: 1200,
            type: 'bandpass',
            Q: 2
        }).connect(this.clapGain);

        this.clap = new Tone.NoiseSynth({
            noise: { type: 'white' },
            envelope: {
                attack: 0.001,
                decay: this.params.clapDecay,
                sustain: 0,
                release: 0.08
            }
        }).connect(this.clapFilter);

        // === RIMSHOT ===
        this.rimshotGain = new Tone.Gain(this.params.rimshotLevel).connect(this.masterChannel);

        this.rimshot = new Tone.MembraneSynth({
            pitchDecay: 0.008,
            octaves: 2,
            oscillator: { type: 'triangle' },
            envelope: {
                attack: 0.001,
                decay: 0.05,
                sustain: 0,
                release: 0.02
            }
        }).connect(this.rimshotGain);

        // === HI-HAT (closed) ===
        this.hhGain = new Tone.Gain(this.params.hhLevel).connect(this.masterChannel);

        this.hihatFilter = new Tone.Filter({
            frequency: 8000,
            type: 'highpass'
        }).connect(this.hhGain);

        this.hihat = new Tone.NoiseSynth({
            noise: { type: 'white' },
            envelope: {
                attack: 0.001,
                decay: this.params.hhDecay,
                sustain: 0,
                release: 0.01
            }
        }).connect(this.hihatFilter);

        // === OPEN HI-HAT ===
        this.openHHGain = new Tone.Gain(this.params.openHHLevel).connect(this.masterChannel);

        this.openHHFilter = new Tone.Filter({
            frequency: 8000,
            type: 'highpass'
        }).connect(this.openHHGain);

        this.openHihat = new Tone.NoiseSynth({
            noise: { type: 'white' },
            envelope: {
                attack: 0.001,
                decay: this.params.openHHDecay,
                sustain: 0,
                release: 0.1
            }
        }).connect(this.openHHFilter);

        // === LOW TOM ===
        this.lowTomGain = new Tone.Gain(this.params.lowTomLevel).connect(this.masterChannel);

        this.lowTom = new Tone.MembraneSynth({
            pitchDecay: 0.08,
            octaves: 4,
            oscillator: { type: 'sine' },
            envelope: {
                attack: 0.001,
                decay: 0.4,
                sustain: 0,
                release: 0.1
            }
        }).connect(this.lowTomGain);

        // === MID TOM ===
        this.midTomGain = new Tone.Gain(this.params.midTomLevel).connect(this.masterChannel);

        this.midTom = new Tone.MembraneSynth({
            pitchDecay: 0.06,
            octaves: 4,
            oscillator: { type: 'sine' },
            envelope: {
                attack: 0.001,
                decay: 0.3,
                sustain: 0,
                release: 0.1
            }
        }).connect(this.midTomGain);

        // === HI TOM ===
        this.hiTomGain = new Tone.Gain(this.params.hiTomLevel).connect(this.masterChannel);

        this.hiTom = new Tone.MembraneSynth({
            pitchDecay: 0.04,
            octaves: 3,
            oscillator: { type: 'sine' },
            envelope: {
                attack: 0.001,
                decay: 0.25,
                sustain: 0,
                release: 0.1
            }
        }).connect(this.hiTomGain);

        // === COWBELL ===
        this.cowbellGain = new Tone.Gain(this.params.cowbellLevel).connect(this.masterChannel);

        this.cowbellOsc1 = new Tone.Oscillator({
            frequency: 560,
            type: 'square'
        });
        this.cowbellOsc2 = new Tone.Oscillator({
            frequency: 845,
            type: 'square'
        });
        this.cowbellEnv = new Tone.AmplitudeEnvelope({
            attack: 0.001,
            decay: 0.3,
            sustain: 0,
            release: 0.1
        }).connect(this.cowbellGain);

        this.cowbellFilter = new Tone.Filter({
            frequency: 3000,
            type: 'bandpass',
            Q: 3
        }).connect(this.cowbellEnv);

        this.cowbellOsc1.connect(this.cowbellFilter);
        this.cowbellOsc2.connect(this.cowbellFilter);
        this._startOscillators();
    }

    _startOscillators() {
        this.cowbellOsc1.start();
        this.cowbellOsc2.start();
    }

    _stopOscillators() {
        this.cowbellOsc1.stop();
        this.cowbellOsc2.stop();
    }

    // --- Méthodes de déclenchement des sons ---

    triggerKick(time) {
        this.kick.triggerAttackRelease(this.params.kickTone, '8n', time);
    }

    triggerSnare(time) {
        this.snareNoise.triggerAttackRelease('8n', time);
        this.snareTone.triggerAttackRelease('C3', '16n', time);
    }

    triggerHihat(time) {
        this.hihat.triggerAttackRelease('16n', time);
    }

    triggerOpenHihat(time) {
        this.openHihat.triggerAttackRelease('8n', time);
    }

    triggerClap(time) {
        // Trigger multiple times for the characteristic 808 clap sound
        this.clap.triggerAttackRelease('16n', time);
        this.clap.triggerAttackRelease('32n', time + 0.01);
        this.clap.triggerAttackRelease('32n', time + 0.02);
    }

    triggerLowTom(time) {
        // Tune: 0=60Hz, 1=120Hz
        const freq = 60 + this.params.lowTomTune * 60;
        this.lowTom.triggerAttackRelease(freq, '8n', time);
    }

    triggerMidTom(time) {
        // Tune: 0=100Hz, 1=180Hz
        const freq = 100 + this.params.midTomTune * 80;
        this.midTom.triggerAttackRelease(freq, '8n', time);
    }

    triggerHiTom(time) {
        // Tune: 0=140Hz, 1=240Hz
        const freq = 140 + this.params.hiTomTune * 100;
        this.hiTom.triggerAttackRelease(freq, '8n', time);
    }

    triggerRimshot(time) {
        // Tune: 0=800Hz, 1=1600Hz
        const freq = 800 + this.params.rimshotTune * 800;
        this.rimshot.triggerAttackRelease(freq, '32n', time);
    }

    triggerCowbell(time) {
        this.cowbellEnv.triggerAttackRelease('8n', time);
    }

    // --- Setters pour les paramètres ---

    // KICK
    setKickLevel(value) {
        this.params.kickLevel = value;
        this.kickGain.gain.value = value;
    }
    setKickTone(value) {
        this.params.kickTone = value;
    }
    setKickDecay(value) {
        this.params.kickDecay = value;
        this.kick.envelope.decay = value;
    }

    // SNARE
    setSnareLevel(value) {
        this.params.snareLevel = value;
        this.snareGain.gain.value = value;
    }
    setSnareTone(value) {
        this.params.snareTone = value;
        // Tone affecte le pitch de la partie tonale
        this.snareTone.volume.value = -15 + value * 10; // -15dB à -5dB
    }
    setSnareSnappy(value) {
        this.params.snareSnappy = value;
        this.snareNoise.envelope.decay = 0.1 + value * 0.2;
    }

    // CLAP
    setClapLevel(value) {
        this.params.clapLevel = value;
        this.clapGain.gain.value = value;
    }
    setClapDecay(value) {
        this.params.clapDecay = value;
        this.clap.envelope.decay = value;
    }

    // RIMSHOT
    setRimshotLevel(value) {
        this.params.rimshotLevel = value;
        this.rimshotGain.gain.value = value;
    }
    setRimshotTune(value) {
        this.params.rimshotTune = value;
    }

    // HI-HAT (closed)
    setHHLevel(value) {
        this.params.hhLevel = value;
        this.hhGain.gain.value = value;
    }
    setHihatDecay(value) {
        this.params.hhDecay = value;
        this.hihat.envelope.decay = value;
    }

    // OPEN HI-HAT
    setOpenHHLevel(value) {
        this.params.openHHLevel = value;
        this.openHHGain.gain.value = value;
    }
    setOpenHHDecay(value) {
        this.params.openHHDecay = value;
        this.openHihat.envelope.decay = value;
    }

    // LOW TOM
    setLowTomLevel(value) {
        this.params.lowTomLevel = value;
        this.lowTomGain.gain.value = value;
    }
    setLowTomTune(value) {
        this.params.lowTomTune = value;
    }

    // MID TOM
    setMidTomLevel(value) {
        this.params.midTomLevel = value;
        this.midTomGain.gain.value = value;
    }
    setMidTomTune(value) {
        this.params.midTomTune = value;
    }

    // HI TOM
    setHiTomLevel(value) {
        this.params.hiTomLevel = value;
        this.hiTomGain.gain.value = value;
    }
    setHiTomTune(value) {
        this.params.hiTomTune = value;
    }

    // COWBELL
    setCowbellLevel(value) {
        this.params.cowbellLevel = value;
        this.cowbellGain.gain.value = value;
    }

    // --- Getters / setParams / getParams ---

    getParams() {
        return { ...this.params };
    }

    setParams(params) {
        if (params.kickLevel !== undefined) this.setKickLevel(params.kickLevel);
        if (params.kickTone !== undefined) this.setKickTone(params.kickTone);
        if (params.kickDecay !== undefined) this.setKickDecay(params.kickDecay);
        if (params.snareLevel !== undefined) this.setSnareLevel(params.snareLevel);
        if (params.snareTone !== undefined) this.setSnareTone(params.snareTone);
        if (params.snareSnappy !== undefined) this.setSnareSnappy(params.snareSnappy);
        if (params.clapLevel !== undefined) this.setClapLevel(params.clapLevel);
        if (params.clapDecay !== undefined) this.setClapDecay(params.clapDecay);
        if (params.rimshotLevel !== undefined) this.setRimshotLevel(params.rimshotLevel);
        if (params.rimshotTune !== undefined) this.setRimshotTune(params.rimshotTune);
        if (params.hhLevel !== undefined) this.setHHLevel(params.hhLevel);
        if (params.hhDecay !== undefined) this.setHihatDecay(params.hhDecay);
        if (params.openHHLevel !== undefined) this.setOpenHHLevel(params.openHHLevel);
        if (params.openHHDecay !== undefined) this.setOpenHHDecay(params.openHHDecay);
        if (params.lowTomLevel !== undefined) this.setLowTomLevel(params.lowTomLevel);
        if (params.lowTomTune !== undefined) this.setLowTomTune(params.lowTomTune);
        if (params.midTomLevel !== undefined) this.setMidTomLevel(params.midTomLevel);
        if (params.midTomTune !== undefined) this.setMidTomTune(params.midTomTune);
        if (params.hiTomLevel !== undefined) this.setHiTomLevel(params.hiTomLevel);
        if (params.hiTomTune !== undefined) this.setHiTomTune(params.hiTomTune);
        if (params.cowbellLevel !== undefined) this.setCowbellLevel(params.cowbellLevel);
    }

    // Legacy getters for compatibility
    get kickDecay() { return this.params.kickDecay; }
    get kickTone() { return this.params.kickTone; }
    get snareSnappy() { return this.params.snareSnappy; }
    get hhDecay() { return this.params.hhDecay; }

    // --- Power ON/OFF ---

    setPower(on) {
        if (on === this.isPowered) return;

        this.isPowered = on;

        if (on) {
            this.masterChannel.connect(this.mixerChannel);
            if (this.performance.lowPower) {
                this._startOscillators();
            }
        } else {
            this.masterChannel.disconnect();
            if (this.performance.lowPower) {
                this._stopOscillators();
            }
        }
    }

    getPower() {
        return this.isPowered;
    }
}
